import { t } from "i18next";
import { FC, useEffect, useState } from "react";

// Helpers
import { capitalizeWords } from "@/_helpers/text_functions";
import { formatDecimal, roundNumber } from "@/_helpers/number_functions";

// Services and interfaces
import { IRecipe } from "@/interfaces/recipe/recipe";

// Styles
import { Fire, Timer } from "@carbon/icons-react";
import RecipePlaceHolder from "@/assets/recipes/recipe_placeholder.png";

// Components
import Tag from "@/components/tag";
import { Box, Popover, useMediaQuery, useTheme } from "@mui/material";
import MacroProgressWheel from "@/components/nutrition_builder/widgets/macro_wheel";


interface _RecipePopoverProps {
    open: boolean;
    onClose: () => void;
    anchorEl: HTMLElement|null;
    recipe: IRecipe;
}

const RecipePopover: FC<_RecipePopoverProps> = ({ 
    open,
    onClose,
    anchorEl,
    recipe,
}) => {

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));

    const popoverWidth = isSmallScreen ? 280 : isMediumScreen ? 320 : 380;
    const offset = 16;

    const [anchorPosition, setAnchorPosition] = useState<{ vertical: "top"; horizontal: "right" | "left" }>({
        vertical: "top",
        horizontal: "right",
    });

    const calories = roundNumber(recipe.calories / recipe.servings, 0);
    const protein = roundNumber(recipe.protein / recipe.servings, 1);
    const carbs = roundNumber(recipe.carbs / recipe.servings, 1);
    const fat = roundNumber(recipe.fat / recipe.servings, 1);

    useEffect(() => {
        if (anchorEl) {

            const rect = anchorEl.getBoundingClientRect();
            const spaceRight = window.innerWidth - rect.right;
            const spaceLeft = rect.left;

            if (spaceRight >= popoverWidth + offset) {
                setAnchorPosition({ vertical: "top", horizontal: "right" });
            } else if (spaceLeft >= popoverWidth + offset) {
                setAnchorPosition({ vertical: "top", horizontal: "left" });
            } else {
                setAnchorPosition({ vertical: "top", horizontal: "right" });
            }
        }
    }, [anchorEl, popoverWidth]);
    
    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{
                vertical: "top",
                horizontal: anchorPosition.horizontal,
            }}
            transformOrigin={{
                vertical: "top",
                horizontal: anchorPosition.horizontal === "right" ? "left" : "right",
            }}
            disableRestoreFocus
            sx={{
                pointerEvents: "none",
                marginLeft: anchorPosition.horizontal === "left" ? `-${popoverWidth + offset + 16}px` : `${offset}px`,
                transform: `translate(${anchorPosition.horizontal === "left" ? popoverWidth + offset : 0}px, 0px)`
            }}
        >
            <Box width={popoverWidth}>
                {/* HEADER */}
                <Box position='relative' width='100%' sx={{
                    aspectRatio: 16/9,
                    background: `linear-gradient(180deg, #FFFFFF00 19%, #1D212D 100%), url(${recipe.image ?? RecipePlaceHolder})`,
                    backgroundSize: 'cover', backgroundPosition: 'center'
                }}>
                    {/* Tags */}
                    <Box className='text-primary' position='absolute' bottom={0} left={0} display='flex' gap={0.5} p={1}>
                        {/* Calories */}
                        <Tag 
                            icon={<Fire />}
                            colour="grey"
                            outlined
                            sx={{borderRadius: '4px !important'}}
                            label={`${roundNumber(recipe.calories, 0)} Kcal`}
                        />

                        <Tag
                            colour="purple"
                            outlined
                            sx={{borderRadius: '4px !important'}}
                            label={`${roundNumber(recipe.protein, 0)}P / ${roundNumber(recipe.carbs, 0)}C / ${roundNumber(recipe.fat, 0)}F`}
                        />
                    </Box>
                </Box>

                {/* Details */}
                <Box pt={1} px={2}>
                    {/* Recipe Title */}
                    <div className="heading-07-compact">{capitalizeWords(recipe.title)}</div>
                    
                    <Box className='label-text-02' height="32px" sx={{
                        color: 'var(--icon-interactive)',
                        display: 'flex', gap: 0.5,
                        alignItems: 'center'
                    }}>
                        <Timer size={16} /> 
                        <span>{roundNumber(recipe.prep_time + recipe.cook_time, 0)} mins</span>
                    </Box>
                    
                    <Box mb={2} mt={1}>
                        <div className="heading-08-compact" style={{paddingBottom: 4}}>{t('components.recipeBuilder.ingredients')}</div>
                        <ul style={{padding: 0, margin: 0, listStyleType: 'none'}}>
                            {recipe.ingredients.map((ingredient) => (
                                <li key={ingredient.id} className="label-text-02" style={{
                                    display: 'flex', paddingTop: 2, paddingBottom: 2,
                                    justifyContent: 'space-between'
                                }}>
                                    <div>{capitalizeWords(ingredient.name)}</div>
                                    <div>{`${formatDecimal(ingredient.amount, 2)} ${capitalizeWords(ingredient.unit.label)}`}</div>
                                </li>
                            ))}
                        </ul>
                    </Box>

                    <Box display='flex' alignItems='center' mb={2}>
                        <MacroProgressWheel
                            calories={calories}
                            protein={protein}
                            carbs={carbs}
                            fat={fat}
                        />
                        <Box className='label-text-02' display='flex' gap={0.5} mx={2}>
                            <span>{`${calories}${t('metrics.kcal')}`}</span>
                            <span style={{color: 'var(--protein)'}}>{`${protein}${t('metrics.proteinLetter')}`}</span>
                            <span style={{color: 'var(--carbs)'}}>{`${carbs}${t('metrics.carbsLetter')}`}</span>
                            <span style={{color: 'var(--fat)'}}>{`${fat}${t('metrics.fatLetter')}`}</span>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Popover>
    );
};

export default RecipePopover;